import React, { useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, DataListItem, toast } from '@node-space/ui-components'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { REWARDS_REFERENCE_PREFIX } from 'pages/Rewards/constants/const'
import { useRewardsSheetContext } from 'pages/Rewards/context/RewardsSheetProvider'
import { useRewardsWithdraw } from 'pages/Rewards/hooks/api/useRewardsWithdraw'
import useRewardsTracking from 'pages/Rewards/hooks/useRewardsTracking'
import {
  formatAmount,
  invalidateRewardsQueries,
  prepareWithdrawReviewData,
} from 'pages/Rewards/utils'
import { generateReference } from 'utils/utils'
import { RewardsWithdrawFormData } from '../../types'
import { RewardsAmountInput } from './RewardsAmountInput'
import { RewardsCurrencySelect } from './RewardsCurrencySelect'
import { RewardsReview } from './RewardsReview'
import { RewardsWalletSelect } from './RewardsWalletSelect'
import { getWithdrawValidationSchema } from './validationSchema'

export type WithdrawMode = 'standard' | 'removeAllocation'
type WithdrawStep = 'form' | 'review'

interface RewardsWithdrawFormProps {
  withdrawType: WithdrawMode
}

export const RewardsWithdraw: React.FC<RewardsWithdrawFormProps> = ({ withdrawType }) => {
  const { t } = useTranslation()
  const { requestError, setRequestError } = useMappedErrorMessage()
  const { trackFormInitiate, trackFormCancel } = useRewardsTracking()
  const reactQueryClient = useQueryClient()
  const {
    rewardsSheetAction,
    handleSheetUpdate,
    selectedCurrency: portfolioCurrency,
    userPortfolios,
    rewardsProducts,
  } = useRewardsSheetContext()

  const isRemoveAllocation = withdrawType === 'removeAllocation'
  const [maxAmount, setMaxAmount] = useState('')
  const [currentStep, setCurrentStep] = useState<WithdrawStep>('form')

  const formMethods = useForm<RewardsWithdrawFormData>({
    resolver: yupResolver(getWithdrawValidationSchema(t, maxAmount)),
    mode: 'onChange',
    defaultValues: {
      currency: portfolioCurrency,
      withdrawToWallet: null,
      amount: '',
    },
  })
  const { handleSubmit, setValue, watch } = formMethods
  const selectedCurrency = watch('currency')
  const selectedWallet = watch('withdrawToWallet')
  const enteredAmount = watch('amount')

  const { onRewardsWithdraw, isRewardsWithdrawPending } = useRewardsWithdraw({
    isRemoveAllocation,
    onSuccess: data => {
      const walletReference = userPortfolios.find(
        portfolio => portfolio.currency === data.currency
      )?.walletId

      invalidateRewardsQueries(reactQueryClient, walletReference)
      rewardsSheetAction?.onClose?.()
      toast({
        variant: 'success',
        header: t(
          isRemoveAllocation
            ? 'rewards.removeAllocation.successNotificationTitle'
            : 'rewards.withdraw.successNotificationTitle',
          {
            walletName: selectedWallet?.name || '',
            currency: `${data.amount} ${data.currency}`,
          }
        ),
      })
    },
    onError: error => {
      setRequestError({
        show: true,
        errorCode: error?.status,
        handledMessage: isRemoveAllocation
          ? t('rewards.errors.removeAllocation')
          : t('rewards.errors.withdrawFromPortfolio'),
      })
    },
  })

  useEffect(() => {
    trackFormInitiate(isRemoveAllocation ? 'removeAllocation' : 'withdraw')
  }, [isRemoveAllocation])

  useEffect(() => {
    if (selectedCurrency) {
      setValue('withdrawToWallet', null)
      setValue('amount', '')
    }
  }, [selectedCurrency, setValue])

  useEffect(() => {
    if (rewardsProducts.length === 1) {
      setMaxAmount(userPortfolios[0]?.balance?.available || '0')
    }
  }, [rewardsProducts])

  useEffect(() => {
    if (rewardsSheetAction) {
      if (currentStep === 'review') {
        handleSheetUpdate({
          header: t('rewards.withdraw.reviewHeader'),
          onBack: onReviewBack,
        })
      } else {
        handleSheetUpdate({
          header: isRemoveAllocation
            ? t('rewards.removeAllocation.header')
            : t('rewards.withdraw.header'),
          onBack: undefined,
        })
      }
    }
  }, [currentStep, isRemoveAllocation, t, handleSheetUpdate, rewardsSheetAction])

  const onCancel = () => {
    trackFormCancel(isRemoveAllocation ? 'removeAllocation' : 'withdraw')
    rewardsSheetAction?.onClose?.()
  }

  const onReviewBack = () => {
    setCurrentStep('form')
  }

  const onSubmit = () => {
    setRequestError(null)
    setCurrentStep('review')
  }

  const onConfirmWithdraw = () => {
    const data = formMethods.getValues()
    const portfolio = userPortfolios.find(portfolio => portfolio.currency === selectedCurrency)

    if (!portfolio) {
      setRequestError({
        show: true,
        errorCode: 400,
        handledMessage: t('rewards.withdraw.portfolioNotFound'),
      })
      return
    }

    onRewardsWithdraw({
      reference: generateReference(REWARDS_REFERENCE_PREFIX),
      portfolioId: portfolio.id,
      counterparty: {
        walletId: data.withdrawToWallet?.value,
        amount: data.amount,
      },
    })
  }

  const setMaxAmountFromPortfolio = (value: string) => {
    setMaxAmount(value)
  }

  const currentPortfolio = useMemo(
    () => userPortfolios.find(p => p.currency === selectedCurrency),
    [userPortfolios, selectedCurrency]
  )

  const blockedBalance = useMemo(
    () => currentPortfolio?.balance?.blocked || '0',
    [currentPortfolio]
  )

  const remainingAmount = useMemo(() => {
    if (!isRemoveAllocation || !enteredAmount) return 0
    const blocked = Number.parseFloat(blockedBalance)
    const amount = Number.parseFloat(enteredAmount)
    return Math.max(0, blocked - amount)
  }, [isRemoveAllocation, blockedBalance, enteredAmount])

  if (currentStep === 'review') {
    return (
      <RewardsReview
        data={prepareWithdrawReviewData(formMethods.getValues(), selectedCurrency, {
          isRemoveAllocation,
          blockedBalance: currentPortfolio?.balance?.blocked || '0',
        })}
        requestError={requestError}
        isPending={isRewardsWithdrawPending}
        onCancel={onCancel}
        onConfirm={onConfirmWithdraw}
      />
    )
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        tag="form"
        flexCol
        gapY={16}
        width="full"
        paddingY={24}
        onSubmit={handleSubmit(onSubmit)}
      >
        {requestError?.show && (
          <Callout variant="error" testId="withdraw-error-callout">
            {requestError?.handledMessage}
          </Callout>
        )}

        {isRemoveAllocation && (
          <Box flexCol>
            <DataListItem
              id="amount-pending-allocation"
              testId="amount-pending-allocation"
              title={t('rewards.removeAllocation.amountPendingAllocation')}
              body={`${formatAmount(Number.parseFloat(blockedBalance))} ${selectedCurrency}`}
            />
          </Box>
        )}

        {rewardsProducts.length > 1 && (
          <RewardsCurrencySelect
            selectedCurrency={selectedCurrency}
            disabled={!!portfolioCurrency || isRewardsWithdrawPending}
            filterByBalance={isRemoveAllocation ? 'blocked' : undefined}
            setMaxAmount={setMaxAmountFromPortfolio}
          />
        )}

        <RewardsWalletSelect
          selectedCurrency={selectedCurrency}
          name="withdrawToWallet"
          label={
            isRemoveAllocation
              ? t('rewards.removeAllocation.sendBackToWallet')
              : t('rewards.withdraw.withdrawToWallet')
          }
          disabled={!selectedCurrency || isRewardsWithdrawPending}
          testId="withdraw-to-wallet-combobox"
        />

        <RewardsAmountInput
          selectedCurrency={selectedCurrency}
          maxAmount={maxAmount}
          disabled={!selectedCurrency || isRewardsWithdrawPending}
          label={isRemoveAllocation ? t('rewards.removeAllocation.amountToSendBack') : t('amount')}
        />

        {isRemoveAllocation && (
          <Box flexCol>
            <DataListItem
              id="amount-remaining"
              testId="amount-remaining"
              title={t('rewards.removeAllocation.amountRemaining')}
              body={`${formatAmount(remainingAmount)} ${selectedCurrency}`}
            />
          </Box>
        )}

        <Box flex justify="between" gapX={12} paddingT={24}>
          <Button onClick={onCancel} variant="secondary" type="button" testId="cancel-button">
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            testId="withdraw-button"
            disabled={isRewardsWithdrawPending}
            loading={isRewardsWithdrawPending}
          >
            {t('rewards.withdrawCurrency', {
              currency: selectedCurrency,
            })}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  )
}
